import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import uniqBy from "lodash/uniqBy";

// Assets and utilities
import images from "~/assets/img";
import * as storage from "~/utils/storage";
import { numberToOrdinalWord } from "~/utils/helpers";
import { useSearchParams } from "~/utils/helpers/react-router-helper";

// UI Components
import {
  Col,
  Row,
  StackProps,
  UiBox,
  UiCenter,
  UiDivider,
  UiFlex,
  UiImage,
  UiPrimaryButton,
  UiSkeletonText,
  UiText,
  useBreakpointValue,
} from "~/components/elements";

// Layout Components
import { PageLayout } from "~/components/layouts/PageLayout";
import { DashboardStepper } from "~/components/shared/DashboardStepper";
import { DashboardMainBox } from "~/components/shared/DashboardMainBox";
import { SelectableGrid } from "~/pages/dashboard/styles-step/SelectableGrid";
import { HeadshotStyleListItem } from "~/pages/result-detail/HeadshotStyleListItem";

// Constants
import { DASHBOARD_STORAGE_KEYS, PAGES } from "~/constants";

// Redux
import { useAppDispatch, useAppSelector, useRequestState } from "~/hooks";
import { RootState } from "~/redux/root-reducer";
import {
  EDashboardStep,
  removeHeadshotStyleSelected,
  setActiveStep,
  setDashboardProgress,
  setHeadshotStyleSelected,
} from "~/redux/dashboards/slice";
import { confirmUpsellJob, fetchJobData, fetchOutfits } from "~/redux/dashboards/thunk"; // Types
import { HeadshotStyleApiItem, OutfitApiItem, PhotoshootType } from "~/types/models/dashboard"; // Utils
import { requestOK } from "redux-thunk-kit"; // Interfaces and Enums

// Interfaces and Enums
export interface SimplePaginatorProps extends StackProps {
  pages: number;
  currentPage: number;
  onPageChange: (page: number) => any;
}

enum SelectionStep {
  SelectOutfit = "outfit",
  SelectBackground = "background",
}

// Helper Functions
const getStorageKey = (isUpsellFlow: boolean, upsellJobId: string | null) => {
  if (isUpsellFlow && upsellJobId) {
    return `${DASHBOARD_STORAGE_KEYS.HEADSHOT_STYLE_SELECTION}_UPSELL_${upsellJobId}`;
  }
  return DASHBOARD_STORAGE_KEYS.HEADSHOT_STYLE_SELECTION;
};

// Helper function for initial selected styles
const loadSelectStyleFromStorage = (isUpsellFlow: boolean, upsellJobId: string | null) => {
  const storageKey = getStorageKey(isUpsellFlow, upsellJobId);
  return storage.load(storageKey) || [];
};

const getDefaultStepNumber = (isUpsellFlow: boolean, upsellJobId: string | null) => {
  const savedStyles = loadSelectStyleFromStorage(isUpsellFlow, upsellJobId);
  return savedStyles.length > 0 ? savedStyles.length : 1;
};

const getLastSelectedBackground = (isUpsellFlow: boolean, upsellJobId: string | null) => {
  const initialSelectedStyles = loadSelectStyleFromStorage(isUpsellFlow, upsellJobId);
  const lastSelectedHeadshotStyle = initialSelectedStyles[initialSelectedStyles.length - 1];
  return lastSelectedHeadshotStyle || null;
};
const getLastSelectedOutfit = (isUpsellFlow: boolean, upsellJobId: string | null, outfitData: OutfitApiItem[] = []) => {
  const initialSelectedStyles = loadSelectStyleFromStorage(isUpsellFlow, upsellJobId);
  const lastSelectedHeadshotStyle = initialSelectedStyles[initialSelectedStyles.length - 1];
  return lastSelectedHeadshotStyle ? outfitData?.find(o => o.id === lastSelectedHeadshotStyle.outfit_id) : null;
};

const getDefaultSubStep = (isUpsellFlow: boolean, upsellJobId: string | null, photoshootType: PhotoshootType) => {
  const initialSelectedStyles = loadSelectStyleFromStorage(isUpsellFlow, upsellJobId);
  const lastSelectedHeadshotStyle = initialSelectedStyles[initialSelectedStyles.length - 1];
  if (lastSelectedHeadshotStyle) {
    return SelectionStep.SelectBackground;
  }
  return photoshootType === "two_person" ? SelectionStep.SelectBackground : SelectionStep.SelectOutfit;
};

// Main Component
export function SelectStylePage() {
  // Hooks initialization
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [searchParams] = useSearchParams();
  const dividerRef = useRef<any>(null);

  // URL parameters
  const isUpsellFlow = searchParams.get("type") === "upsell";
  const upsellJobId = searchParams.get("job_id");

  // Redux selectors
  const headshotStyleSelected = useAppSelector(state => state.dashboard.headshotStyleSelected);
  const currentJob = useSelector((state: RootState) => state.dashboard.jobPendingData);
  const upsellingJob = useAppSelector(state => state.dashboard.jobItemData);
  const outfitData = useSelector((state: RootState) => state.dashboard.isActiveOutfitData);
  const photoshootType = currentJob?.type || upsellingJob?.type || upsellingJob?.package?.type || "one_person";

  // Request states
  const { loading: loadingOutfits } = useRequestState(fetchOutfits);
  const { loading: confirmLoading } = useRequestState(confirmUpsellJob);

  // Responsive values
  const vrTitle = useBreakpointValue<any>({ base: "headline4", md: "headline2" });
  const vrCaption = useBreakpointValue<any>({ base: "body2", md: "body1" });

  // Helper function for available styles calculation
  const getAvailableStyles = () => {
    if (isUpsellFlow && upsellJobId) {
      if (upsellingJob?.upsells) {
        const pendingUpsells = upsellingJob.upsells.filter(upsell => upsell.status === "pending");
        return pendingUpsells.reduce((total, upsell) => total + upsell.max_outfit, 0);
      }
      return 0;
    }

    if (currentJob?.id && !currentJob.package?.id) {
      if (currentJob.max_outfit <= 15) {
        return 5;
      }
      return currentJob.max_outfit || 0;
    }
    return currentJob?.max_outfit || 0;
  };

  // Computed values
  const availableStyles = getAvailableStyles();

  // Local state
  const [currentSubStep, setCurrentSubStep] = useState<SelectionStep>(
    getDefaultSubStep(isUpsellFlow, upsellJobId, photoshootType)
  );
  const [selectedOutfit, setSelectedOutfit] = useState<OutfitApiItem | null>(
    // @ts-ignore
    getLastSelectedOutfit(isUpsellFlow, upsellJobId, outfitData)
  );
  const [selectedBackground, setSelectedBackground] = useState<HeadshotStyleApiItem | null>(
    getLastSelectedBackground(isUpsellFlow, upsellJobId)
  );
  const [currentStepNumber, setCurrentStepNumber] = useState<number>(getDefaultStepNumber(isUpsellFlow, upsellJobId));
  const [stepTitle, setStepTitle] = useState<string>("");

  // Effects, Exclude the headshotStyleSelected to not trigger re-run
  useEffect(() => {
    const storageKey = getStorageKey(isUpsellFlow, upsellJobId);
    const savedStyles = storage.load(storageKey) || [];
    if (JSON.stringify(savedStyles) !== JSON.stringify(headshotStyleSelected)) {
      dispatch(
        setHeadshotStyleSelected({
          styles: savedStyles,
          mode: isUpsellFlow ? "upsell" : "create",
          jobId: isUpsellFlow ? upsellJobId! : undefined,
        })
      );
    }
    // eslint-disable-next-line
  }, [dispatch, isUpsellFlow, upsellJobId]);

  useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchJobData({ id: upsellJobId!, source: "UPSELL_PAGE" }));
    };
    if (upsellJobId) {
      fetchData();
    }
  }, [dispatch, upsellJobId]);

  useEffect(() => {
    if (!outfitData?.length) {
      dispatch(fetchOutfits({}));
    }
  }, [dispatch, outfitData]);

  useEffect(() => {
    if (photoshootType) {
      setCurrentSubStep(getDefaultSubStep(isUpsellFlow, upsellJobId, photoshootType));
    }
  }, [isUpsellFlow, photoshootType, upsellJobId]);

  useEffect(() => {
    dispatch(setActiveStep({ step: 1 }));
  }, [dispatch]);

  useEffect(() => {
    const prefix = isUpsellFlow ? "Add your" : "Choose your";

    if (photoshootType === "two_person") {
      setStepTitle(`${prefix} ${numberToOrdinalWord(currentStepNumber)} background`);
    } else if (currentSubStep === SelectionStep.SelectOutfit) {
      setStepTitle(`${prefix} ${numberToOrdinalWord(currentStepNumber)} outfit`);
    } else {
      setStepTitle(`${prefix} ${numberToOrdinalWord(currentStepNumber)} background`);
    }
  }, [currentSubStep, currentStepNumber, photoshootType, isUpsellFlow]);

  // Utility functions
  const scrollToDivider = () => {
    if (dividerRef.current) {
      dividerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const scrollToBottom = () => {
    window.scrollTo({ top: 9999, behavior: "smooth" });
  };

  const isPreviewingHs = (idx: number) => idx + 1 === headshotStyleSelected?.length && idx + 1 === currentStepNumber;

  const canProceed = () => {
    if (currentSubStep === SelectionStep.SelectOutfit) {
      return selectedOutfit !== null;
    }
    if (currentSubStep === SelectionStep.SelectBackground) {
      return selectedBackground !== null;
    }
    return false;
  };

  const canGoBack = () => {
    if (photoshootType === "two_person") {
      return currentStepNumber > 1 || selectedBackground !== null;
    }
    return (
      currentSubStep === SelectionStep.SelectBackground ||
      (currentSubStep === SelectionStep.SelectOutfit && currentStepNumber > 1)
    );
  };

  const getStepSubtitle = () => {
    if (currentSubStep === SelectionStep.SelectOutfit) {
      return `Pick your ${numberToOrdinalWord(currentStepNumber)} outfit for your photoshoots`;
    }
    if (photoshootType === "two_person") {
      return `Pick your ${numberToOrdinalWord(currentStepNumber)} background for your photoshoots`;
    }
    return `Pick a background to pair with your ${numberToOrdinalWord(currentStepNumber)} outfit`;
  };

  // Event handlers
  const onClickConfirm = async () => {
    if (isUpsellFlow && upsellJobId) {
      const resAction = await dispatch(
        confirmUpsellJob({
          data: { headshot_styles: headshotStyleSelected },
          id: upsellJobId,
        })
      );
      if (requestOK(resAction, confirmUpsellJob)) {
        storage.remove(`${DASHBOARD_STORAGE_KEYS.HEADSHOT_STYLE_SELECTION}_UPSELL_${upsellJobId}`);
        history.push(PAGES.COMPLETED);
      }
    } else {
      dispatch(setDashboardProgress({ step: EDashboardStep.Upload, screenIndex: 0 }));
      history.push(PAGES.KEY_FEATURES);
    }
  };

  const onClickRemoveStyle = (headshotStyle: HeadshotStyleApiItem) => {
    dispatch(
      removeHeadshotStyleSelected({
        style: headshotStyle,
        mode: isUpsellFlow ? "upsell" : "create",
        jobId: isUpsellFlow ? upsellJobId! : undefined,
      })
    );
  };

  const onClickOutfit = (outfit: OutfitApiItem) => {
    setSelectedOutfit(outfit);
    setTimeout(scrollToBottom, 0);
  };

  const onClickBackground = (background: HeadshotStyleApiItem) => {
    if (selectedBackground) {
      const lastHeadshotStyle = headshotStyleSelected[headshotStyleSelected.length - 1];
      dispatch(
        removeHeadshotStyleSelected({
          style: lastHeadshotStyle,
          mode: isUpsellFlow ? "upsell" : "create",
          jobId: isUpsellFlow ? upsellJobId! : undefined,
        })
      );
      dispatch(
        setHeadshotStyleSelected({
          styles: background,
          mode: isUpsellFlow ? "upsell" : "create",
          jobId: isUpsellFlow ? upsellJobId! : undefined,
        })
      );
    } else {
      dispatch(
        setHeadshotStyleSelected({
          styles: background,
          mode: isUpsellFlow ? "upsell" : "create",
          jobId: isUpsellFlow ? upsellJobId! : undefined,
        })
      );
    }
    setSelectedBackground(background);
    setTimeout(scrollToBottom, 0);
  };

  const onClickNext = () => {
    if (currentSubStep === SelectionStep.SelectOutfit && selectedOutfit) {
      setCurrentSubStep(SelectionStep.SelectBackground);
      setSelectedBackground(null);
      setTimeout(scrollToDivider, 0);
    } else if (currentSubStep === SelectionStep.SelectBackground && selectedBackground) {
      if (currentStepNumber >= availableStyles) {
        onClickConfirm();
      } else {
        setCurrentStepNumber(currentStepNumber + 1);

        if (photoshootType === "two_person") {
          setSelectedBackground(null);
        } else {
          setCurrentSubStep(SelectionStep.SelectOutfit);
          setSelectedOutfit(null);
          setSelectedBackground(null);
        }
        setTimeout(scrollToDivider, 0);
      }
    }
  };

  const onClickBack = () => {
    const lastHeadshotStyle = headshotStyleSelected[headshotStyleSelected.length - 1];
    const beforeLastHeadshotStyle = headshotStyleSelected?.[headshotStyleSelected.length - 2];

    if (currentSubStep === SelectionStep.SelectBackground) {
      if (photoshootType === "two_person") {
        setCurrentStepNumber(currentStepNumber > 1 ? currentStepNumber - 1 : 1);
        if (selectedBackground) {
          dispatch(
            removeHeadshotStyleSelected({
              style: lastHeadshotStyle,
              mode: isUpsellFlow ? "upsell" : "create",
              jobId: isUpsellFlow ? upsellJobId! : undefined,
            })
          );
        }
        setSelectedBackground(selectedBackground ? beforeLastHeadshotStyle || null : lastHeadshotStyle);
      } else {
        if (selectedBackground) {
          dispatch(
            removeHeadshotStyleSelected({
              style: lastHeadshotStyle,
              mode: isUpsellFlow ? "upsell" : "create",
              jobId: isUpsellFlow ? upsellJobId! : undefined,
            })
          );
        }
        setCurrentSubStep(SelectionStep.SelectOutfit);
        setSelectedBackground(null);
      }
      setTimeout(scrollToDivider, 0);
    } else if (currentSubStep === SelectionStep.SelectOutfit && currentStepNumber > 1) {
      setCurrentStepNumber(currentStepNumber - 1);

      const outfitWithStyle = outfitData.find(outfit =>
        outfit.is_active_headshot_styles?.some(style => style.headshot_style_id === lastHeadshotStyle.headshot_style_id)
      );

      if (outfitWithStyle) {
        setSelectedOutfit(outfitWithStyle);
        setSelectedBackground(lastHeadshotStyle);
        setCurrentSubStep(SelectionStep.SelectBackground);
        setTimeout(scrollToDivider, 0);
      }
    }
  };

  function renderHSPlaceholder() {
    if (
      (photoshootType === "one_person" && (currentSubStep === SelectionStep.SelectOutfit || !selectedBackground)) ||
      (photoshootType === "two_person" && !selectedBackground)
    ) {
      return (
        <Row
          rounded="xl"
          bg="white"
          borderWidth="1px"
          borderStyle="dashed"
          borderColor="blue"
          p={2}
          w="full"
          maxW={{ base: undefined, md: "19.5rem" }}
          minW="14.9rem"
        >
          <Row h="2.625rem">
            {photoshootType === "one_person" && (
              <UiText vr="title2" noOfLines={1} ml={3}>
                {selectedOutfit?.name || ""}
              </UiText>
            )}
          </Row>
        </Row>
      );
    }

    return null;
  }

  // Render
  return (
    <PageLayout
      metaTagsConfig={{
        title: `Bettershot - ${isUpsellFlow ? "Add more styles" : "Select styles"}`,
        description: `Get 120 professional photoshoots in 3 hours using AI. Elevate your personal brand with corporate photoshoots.`,
        charset: "utf8",
        lang: "en",
        metas: [
          {
            name: "keywords",
            content: "corporate photoshoot,professional photoshoot,bettershot ai,linkedin profile photo,ai photoshoot",
          },
          { name: "robots", content: "noindex,nofollow" },
        ],
        links: [{ rel: "canonical", href: window.location.href }],
      }}
      as={Col}
      flex={1}
      isInGeneratingStep={!isUpsellFlow}
    >
      <Col flex={1}>
        <UiCenter p={3} gap={5} flexDirection={{ base: "column-reverse", lg: "row" }}>
          <DashboardMainBox>
            <UiBox w="full">
              <UiText vr={vrTitle} textAlign="center">
                {stepTitle}
              </UiText>
              <UiText vr={vrCaption} mt={{ base: 2, sm: 3 }} textAlign="center">
                {getStepSubtitle()}
              </UiText>
              {isUpsellFlow && (
                <UiText vr="body3" mt={2} textAlign="center" color="neutral.gray500">
                  You can select {availableStyles} additional styles with your upsell package
                </UiText>
              )}
            </UiBox>

            {/* Selected photoshoot styles section */}
            <Col rounded="3xl" bg="lightblue" p={{ base: 4, sm: 5 }} w="full" mt={{ base: 4, sm: 8 }}>
              <UiFlex
                flexDirection={{ base: "column", lg: "row" }}
                justify="space-between"
                align={{ base: undefined, sm: "center" }}
                gap={1}
              >
                <UiText vr="title1">{isUpsellFlow ? "Additional photoshoot styles" : "Your photoshoot style"}</UiText>
                <UiText>
                  You have selected
                  <UiBox as="span" color="blue" fontWeight="700">
                    &nbsp;{headshotStyleSelected?.length || 0}/{availableStyles} styles
                  </UiBox>
                </UiText>
              </UiFlex>

              {headshotStyleSelected?.length === 0 ? (
                <Col
                  mt={4}
                  p={2}
                  rounded="xl"
                  borderStyle="dashed"
                  borderWidth="1px"
                  borderColor={headshotStyleSelected?.length > 0 ? "transparent" : "neutral.gray200"}
                  align="center"
                >
                  <UiImage src={images.PicturePlaceholder} width="6" height="6" objectFit="cover" />
                  <UiText vr="body3" mt={1}>
                    Your selected photoshoot style will appear here
                  </UiText>
                </Col>
              ) : (
                <Row
                  gap={3}
                  flexWrap={{ base: "nowrap", md: "wrap" }}
                  w="full"
                  mt={4}
                  pb={2}
                  overflowX="auto"
                  sx={{
                    "&::-webkit-scrollbar": {
                      height: "2px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "blue",
                      borderRadius: "20px",
                    },
                  }}
                >
                  {headshotStyleSelected.map((s, idx) => (
                    <HeadshotStyleListItem
                      key={s.headshot_style_id}
                      headshotStyle={s}
                      filterable={false}
                      maxW={{ base: undefined, md: "19.5rem" }}
                      closeable={!isPreviewingHs(idx)}
                      photoshootType={photoshootType}
                      onClickClose={() => {
                        if (photoshootType === "two_person") {
                          // setSelectedBackground(null);
                        }
                        setCurrentStepNumber(currentStepNumber - 1);
                        setTimeout(scrollToDivider, 0);
                        onClickRemoveStyle(s);
                      }}
                    />
                  ))}
                  {renderHSPlaceholder()}
                </Row>
              )}
            </Col>
            <UiDivider
              ref={dividerRef}
              text={currentSubStep === SelectionStep.SelectOutfit ? "Available outfits" : "Available styles"}
              containerProps={{ my: 5, w: "full" }}
            />

            <UiSkeletonText isLoaded={!loadingOutfits} w="full">
              {/* Outfit selection */}
              {currentSubStep === SelectionStep.SelectOutfit && (
                <SelectableGrid items={outfitData} selectedItem={selectedOutfit} onItemClick={onClickOutfit} />
              )}

              {/* Background selection */}
              {currentSubStep === SelectionStep.SelectBackground &&
                (selectedOutfit || photoshootType === "two_person") && (
                  <SelectableGrid
                    items={
                      photoshootType === "two_person" && !selectedOutfit
                        ? uniqBy(
                            outfitData?.flatMap(outfit => outfit.is_active_headshot_styles || []),
                            "id"
                          ) || []
                        : selectedOutfit?.is_active_headshot_styles || []
                    }
                    selectedItems={headshotStyleSelected}
                    selectedItem={selectedBackground}
                    onItemClick={onClickBackground}
                  />
                )}

              {/* Navigation buttons */}
              <Row
                gap={{ base: 2, sm: 5 }}
                flexDirection={{ base: "column", md: "row-reverse" }}
                align="center"
                justify="center"
                mt={8}
                w="full"
              >
                <UiPrimaryButton
                  w="full"
                  maxW={{ base: undefined, md: "16.25rem" }}
                  isLoading={confirmLoading}
                  isDisabled={!canProceed()}
                  onClick={onClickNext}
                >
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {currentStepNumber >= availableStyles ? (isUpsellFlow ? "Complete" : "Next") : "Next"}
                </UiPrimaryButton>
                <UiPrimaryButton
                  w="full"
                  maxW={{ base: undefined, md: "16.25rem" }}
                  bg="white"
                  color="neutral.gray900"
                  _hover={{
                    bg: "white",
                    color: "neutral.gray900",
                  }}
                  border="1px solid #16161A"
                  _disabled={{
                    color: "neutral.gray400",
                    borderWidth: 0,
                    bg: "neutral.gray200",
                  }}
                  isLoading={confirmLoading}
                  isDisabled={!canGoBack()}
                  onClick={onClickBack}
                >
                  Back
                </UiPrimaryButton>
              </Row>
            </UiSkeletonText>
          </DashboardMainBox>

          {/* Dashboard stepper - hidden during upsell flow */}
          {!isUpsellFlow && <DashboardStepper />}
        </UiCenter>
      </Col>
    </PageLayout>
  );
}
